import axios, { AxiosInstance } from 'axios';
import { API_BASE_URL, STORAGE_KEYS } from '../constants';
import type {
  AgentCreditPurchaseData,
  AgentWalletLedgerEntry,
  AgentDashboardData,
  AgentLoginResponse,
  AgentShop,
  AgentTransactionsPagination,
  AgentPlayer,
  AgentPlayerDetail,
  AgentPlayerWalletTransaction,
  AgentShopWalletSummary,
  ShopWalletLedgerRow,
  ShopUsdWalletLedgerResponse,
  ZeroxStaticWallet,
} from '../types';

function createClient(): AxiosInstance {
  const client = axios.create({
    baseURL: API_BASE_URL,
    timeout: 15000,
    headers: { 'Content-Type': 'application/json' },
  });

  client.interceptors.request.use((config) => {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  client.interceptors.response.use(
    (res) => res,
    (error) => {
      if (error.response?.status === 401) {
        window.dispatchEvent(new Event('auth:unauthorized'));
      }
      return Promise.reject(error);
    }
  );

  return client;
}

const client = createClient();

export async function getProviderFeeRate(params: {
  provider: string;
  operation: 'deposit' | 'withdrawal' | 'transfer';
  methodId?: string;
}): Promise<{ success: boolean; data?: { feeRate: number; matchedMethodId?: string | null }; message?: string }> {
  const { data } = await client.get('/payment/provider-fee-rate', {
    params: {
      provider: params.provider,
      operation: params.operation,
      methodId: params.methodId ?? 'all',
    },
  });
  return data;
}

export async function fetchProviderFeeConfigs(): Promise<{
  success: boolean;
  data?: {
    items: Array<{
      id: string;
      provider: string;
      operation: string;
      methodId: string;
      paymentGateFeeRate?: number;
      systemFeeRate?: number;
      feeRate: number;
      networkFeeType?: 'none' | 'percent' | 'fixed';
      networkFeeRate?: number;
      networkFeeAmount?: number;
      isEnabled?: boolean;
    }>;
    providers?: string[];
    supportedCatalog?: Record<
      string,
      { methods?: Array<{ methodId?: string; id?: string; name?: string; ticker?: string; currency?: string }> }
    >;
  };
  message?: string;
}> {
  const { data } = await client.get('/agent/provider-fees');
  return data;
}

export async function loginWithUsernameOrEmail(
  usernameOrEmail: string,
  password: string,
  captchaToken?: string
): Promise<AgentLoginResponse> {
  const loginId = usernameOrEmail.trim();
  const { data } = await client.post<AgentLoginResponse>('/agent-auth/login', {
    username: loginId,
    email: loginId,
    password,
    ...(captchaToken ? { captchaToken } : {}),
  });
  return data;
}

export async function verifyPortalLoginOtp(
  pendingToken: string,
  otp: string
): Promise<AgentLoginResponse> {
  const { data } = await client.post<AgentLoginResponse>('/agent-auth/verify-otp', {
    pendingToken,
    otp,
  });
  return data;
}

export async function patchPortalTwoFactor(payload: {
  two_factor_enabled: boolean;
  two_factor_channel?: 'email' | 'phone';
}): Promise<{
  success: boolean;
  message?: string;
  data?: {
    two_factor_enabled: boolean;
    two_factor_channel?: 'email' | 'phone' | null;
    email_verified?: boolean;
    phone_verified?: boolean;
  };
}> {
  const { data } = await client.patch('/agent/settings/two-factor', payload);
  return data;
}

export type ForgotPasswordRequestResponse = {
  success: boolean;
  message?: string;
  data?: {
    token: string;
    channel: 'email' | 'phone';
    expiresInSeconds: number;
    next_resend_at?: string | null;
  };
};

export async function requestForgotPasswordOtp(contact: string): Promise<ForgotPasswordRequestResponse> {
  const { data } = await client.post('/agent-auth/forgot-password/request-otp', { contact });
  return data;
}

export async function verifyForgotPasswordOtp(
  token: string,
  otp: string
): Promise<{ success: boolean; message?: string; data?: { token: string } }> {
  const { data } = await client.post('/agent-auth/forgot-password/verify-otp', { token, otp });
  return data;
}

export async function confirmForgotPasswordReset(payload: {
  token: string;
  otp: string;
  newPassword: string;
  confirmPassword: string;
}): Promise<{ success: boolean; message?: string }> {
  const { data } = await client.post('/agent-auth/forgot-password/confirm', payload);
  return data;
}

export async function fetchDashboard(): Promise<{
  success: boolean;
  data?: AgentDashboardData;
}> {
  const { data } = await client.get('/agent/dashboard');
  return data;
}

export type DashboardV2Period = 'today' | 'week' | 'month';
export type DashboardKpi = { value: number; previous?: number; deltaPct?: number | null };

export type PortalDashboardV2Envelope = {
  period: DashboardV2Period;
  range_start: string;
  range_end: string;
  timezone: string;
  data_as_of: string;
  comparison: { range_start: string; range_end: string };
};

export type AgentDashboardOverviewData = PortalDashboardV2Envelope & {
  kpis: {
    netPosition: DashboardKpi;
    earnedTotal: DashboardKpi;
    spentTotal: DashboardKpi;
    creditBalance: DashboardKpi;
    usdBalanceNet: DashboardKpi;
    playerMoneyIn: DashboardKpi;
    onlineDepositsCount: DashboardKpi;
    onlineWithdrawalsCount: DashboardKpi;
    onlineDepositsNetUsd: DashboardKpi;
    onlineWithdrawalsGrossUsd: DashboardKpi;
  };
  extras: {
    shopsCount: number;
    roadblocksBadgeCount: number;
    playerMoneyIn: number;
    cashSettlement: {
      expected: number;
      received: number;
      uncollected: number;
    };
  };
  charts: {
    dailyPlayerFlow: Array<{ date: string; usdIn: number; payoutsOut: number; net: number }>;
    topShops: Array<{ id: string; name: string; creditsPurchased: number; cashExpected: number; cashReceived: number }>;
  };
};

export type AgentDashboardFinancialData = PortalDashboardV2Envelope & {
  kpis: {
    netPosition: DashboardKpi;
    earnedTotal: DashboardKpi;
    spentTotal: DashboardKpi;
    cashFromShops: DashboardKpi;
    cdnSoldToShops: DashboardKpi;
    convertsFromShops: DashboardKpi;
    creditsPurchasedUsd: DashboardKpi;
    cashPaidUpstream: DashboardKpi;
    depositAttribution: DashboardKpi;
    counterMargin: DashboardKpi;
    payoutFunding: DashboardKpi;
    playerMoneyIn: DashboardKpi;
    onlineDepositsNetUsd: DashboardKpi;
    onlineDepositsCount: DashboardKpi;
    onlineWithdrawalsGrossUsd: DashboardKpi;
    onlineWithdrawalsCount: DashboardKpi;
    counterDepositsCashReceived: DashboardKpi;
    counterRedemptionsCashPaid: DashboardKpi;
    tierFundingUsd: DashboardKpi;
  };
  charts: {
    dailyNetPosition: Array<{ date: string; earned: number; spent: number; net: number }>;
    earnedMix: Array<{ name: string; value: number }>;
  };
};

export type AgentDashboardShopsData = PortalDashboardV2Envelope & {
  shops: Array<{
    id: string;
    name: string;
    status: string;
    creditsPurchased: DashboardKpi;
    cashExpected: number;
    cashReceived: number;
    networkDeposits: number;
    networkWithdrawals: number;
    counterDeposits: number;
    counterRedemptions: number;
  }>;
  summary: { creditsPurchased: number; cashExpected: number; cashReceived: number };
  pagination: { page: number; limit: number; total: number; totalPages: number };
};

export type AgentDashboardNetworkData = PortalDashboardV2Envelope & {
  kpis: {
    playerMoneyIn: DashboardKpi;
    onlineDepositsNetUsd: DashboardKpi;
    onlineDepositsCount: DashboardKpi;
    onlineWithdrawalsGrossUsd: DashboardKpi;
    onlineWithdrawalsCount: DashboardKpi;
    counterDepositsCashReceived: DashboardKpi;
    counterRedemptionsCashPaid: DashboardKpi;
    tierFundingUsd: DashboardKpi;
  };
};

export type AgentDashboardRoadblocksData = PortalDashboardV2Envelope & {
  roadblocks: Array<{ id: string; label: string; count: number; amountUsd: number; link: string }>;
  badgeCount: number;
};

async function fetchAgentDashboardV2Tab<T>(
  tab: string,
  period: DashboardV2Period,
  refresh = false,
  extra?: Record<string, string | number>,
): Promise<T> {
  const { data } = await client.get(`/agent/dashboard/${tab}`, {
    params: { period, ...(refresh ? { refresh: 1 } : {}), ...extra },
  });
  if (!data?.success || !data.data) throw new Error(data?.message || 'Failed to load dashboard');
  return data.data as T;
}

export const fetchDashboardV2Overview = (period: DashboardV2Period, refresh = false, tz?: string) =>
  fetchAgentDashboardV2Tab<AgentDashboardOverviewData>('overview', period, refresh, tz ? { tz } : undefined);

export const fetchDashboardV2Financial = (period: DashboardV2Period, refresh = false, tz?: string) =>
  fetchAgentDashboardV2Tab<AgentDashboardFinancialData>('financial', period, refresh, tz ? { tz } : undefined);

export const fetchDashboardV2Shops = (
  period: DashboardV2Period,
  params: { page?: number; tz?: string },
  refresh = false,
) =>
  fetchAgentDashboardV2Tab<AgentDashboardShopsData>('shops', period, refresh, {
    page: params.page ?? 1,
    ...(params.tz ? { tz: params.tz } : {}),
  });

export const fetchDashboardV2Network = (period: DashboardV2Period, refresh = false, tz?: string) =>
  fetchAgentDashboardV2Tab<AgentDashboardNetworkData>('network', period, refresh, tz ? { tz } : undefined);

export const fetchDashboardV2Roadblocks = (period: DashboardV2Period, refresh = false, tz?: string) =>
  fetchAgentDashboardV2Tab<AgentDashboardRoadblocksData>('roadblocks', period, refresh, tz ? { tz } : undefined);

export type BalanceConversionResult = {
  credits_added: number;
  usd_deducted: number;
  cost_rate_applied: number;
  new_credit_balance: number;
  new_online_balance_net: number;
  upstream_usd_credited?: boolean;
};

export async function convertBalanceToCredits(
  amount: number,
  idempotencyKey?: string
): Promise<{ success: boolean; data?: BalanceConversionResult; message?: string }> {
  const body: { amount: number; idempotency_key?: string } = { amount };
  if (idempotencyKey && idempotencyKey.trim()) body.idempotency_key = idempotencyKey.trim();
  const { data } = await client.post('/agent/convert-balance-to-credits', body);
  return data;
}

export async function fetchAgentOnlineBalanceLedger(params?: {
  currency?: string;
  type?: string;
  status?: string;
  query?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
  offset?: number;
  sortBy?: string;
  sortOrder?: string;
}): Promise<{ success: boolean; data?: any; message?: string }> {
  const { data } = await client.get('/agent/online-balance/ledger', { params });
  return data;
}

export async function fetchAgentCreditTransactionById(
  id: string,
): Promise<{ success: boolean; data?: { item: AgentWalletLedgerEntry }; message?: string }> {
  const { data } = await client.get(`/agent/transactions/${id}`);
  return data;
}

export async function fetchAgentUsdLedgerEntryById(
  id: string,
): Promise<{ success: boolean; data?: { item: Record<string, unknown> }; message?: string }> {
  const { data } = await client.get(`/agent/online-balance/ledger/${id}`);
  return data;
}

export type OnlineDebtPayoutNetwork = 'btc' | 'eth' | 'usdt_erc' | 'usdt_trc';

export type WithdrawalOtpEligibility = {
  canWithdraw: boolean;
  emailVerified: boolean;
  phoneVerified: boolean;
  maskedEmail?: string | null;
  maskedPhone?: string | null;
};

export async function fetchAgentWithdrawalOtpEligibility(): Promise<{
  success: boolean;
  data?: WithdrawalOtpEligibility;
  message?: string;
}> {
  const { data } = await client.get('/agent/online-balance/withdrawals/otp/eligibility');
  return data;
}

export async function sendAgentWithdrawalOtp(body: {
  channel: 'email' | 'phone';
}): Promise<{ success: boolean; data?: { next_resend_at?: string }; message?: string }> {
  const { data } = await client.post('/agent/online-balance/withdrawals/otp/send', body);
  return data;
}

export async function verifyAgentWithdrawalOtp(body: {
  channel: 'email' | 'phone';
  otp: string;
}): Promise<{ success: boolean; data?: { withdrawChallengeToken: string }; message?: string }> {
  const { data } = await client.post('/agent/online-balance/withdrawals/otp/verify', body);
  return data;
}

export async function createAgentOnlineBalanceWithdrawal(body: {
  amountUsd: number;
  payoutNetwork: OnlineDebtPayoutNetwork;
  payoutWalletAddress: string;
  note?: string | null;
  idempotencyKey: string;
  withdrawChallengeToken: string;
}): Promise<{ success: boolean; data?: any; message?: string }> {
  const { data } = await client.post('/agent/online-balance/withdrawals', body);
  return data;
}

export type PortalWithdrawalLimitsSnapshot = {
  maxWithdrawalPerTransaction: number;
  maxWithdrawalPerDay: number;
  withdrawnTodayUsd: number;
  remainingTodayUsd: number;
  dailyLimitReached?: boolean;
};

export async function fetchAgentPortalWithdrawalLimits(params?: {
  amountUsd?: number;
}): Promise<{
  success: boolean;
  data?: PortalWithdrawalLimitsSnapshot;
  message?: string;
}> {
  const { data } = await client.get('/agent/portal-withdrawal-limits', { params });
  return data;
}

export async function fetchAgentOnlineBalanceWithdrawalFee(params: {
  payoutNetwork: OnlineDebtPayoutNetwork;
  amountUsd?: number;
}): Promise<{
  success: boolean;
  data?: {
    payoutNetwork: string;
    feeRate: number;
    systemFeeRate?: number;
    gatewayFeeRate?: number;
    paymentGateFeeMinUsd?: number;
    networkFeeType?: 'none' | 'percent' | 'fixed' | string;
    networkFeeRate?: number;
    networkFeeAmount?: number;
    matchedMethodId: string | null;
    feeConfigDisabled?: boolean;
    feeConfigDisabledScope?: string | null;
    feeConfigMessage?: string | null;
    tierFundingUsd?: number;
    gatewayPayoutUsd?: number;
    totalFeeUsd?: number;
    limits?: PortalWithdrawalLimitsSnapshot;
  };
  message?: string;
}> {
  const { data } = await client.get('/agent/online-balance/withdrawals/fee', { params });
  return data;
}

export async function sendAgentVerificationOtp(payload?: {
  channel?: 'email' | 'phone';
}): Promise<{
  success: boolean;
  data?: { resend_count: number; next_resend_at: string; expires_at: string; channel: 'email' | 'phone' };
  message?: string;
  data_localized?: unknown;
}> {
  const { data } = await client.post('/agent/verify/send-otp', payload ?? {});
  return data;
}

export async function confirmAgentVerificationOtp(payload: {
  channel: 'email' | 'phone';
  otp: string;
}): Promise<{
  success: boolean;
  data?: { agent: Record<string, unknown>; verified: boolean; channel: 'email' | 'phone' };
  message?: string;
}> {
  const { data } = await client.post('/agent/verify/confirm', payload);
  return data;
}

export async function patchAgentLowBalanceWarning(payload: {
  low_balance_warning_enabled?: boolean;
  low_balance_threshold_usd?: number;
}): Promise<{
  success: boolean;
  data?: {
    low_balance_warning_enabled: boolean;
    low_balance_threshold_usd: number;
    low_balance_effective_threshold_usd: number;
  };
  message?: string;
}> {
  const { data } = await client.patch('/agent/settings/low-balance-warning', payload);
  return data;
}

export async function fetchAgentTransactions(params?: {
  page?: number;
  limit?: number;
  owner_scope?: 'self';
  type?: string;
  shop_id?: string;
  period?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
  sort?: string;
  sort_dir?: string;
  sortBy?: string;
  sortOrder?: string;
}): Promise<{
  success: boolean;
  data?: {
    items: AgentWalletLedgerEntry[];
    pagination: AgentTransactionsPagination;
    summary: {
      totalTransactions: number;
      totalDeposits: number;
      totalWithdrawals: number;
      netFlow: number;
    };
  };
  message?: string;
}> {
  const { data } = await client.get('/agent/transactions', { params });
  return data;
}

async function getBlobExport(
  url: string,
  params: Record<string, string | number | undefined>,
  fallbackFilename: string,
): Promise<{ blob: Blob; filename: string }> {
  try {
    const response = await client.get<Blob>(url, {
      params,
      responseType: 'blob',
    });
    const disposition = String(response.headers['content-disposition'] || '');
    const match = /filename="([^"]+)"/i.exec(disposition);
    return { blob: response.data, filename: match?.[1] || fallbackFilename };
  } catch (e: unknown) {
    if (axios.isAxiosError(e) && e.response?.data instanceof Blob) {
      try {
        const text = await e.response.data.text();
        const parsed = JSON.parse(text) as { message?: string };
        throw new Error(parsed.message || 'Export failed');
      } catch {
        throw new Error('Export failed');
      }
    }
    throw e;
  }
}

export async function exportAgentWalletTransactions(params: {
  owner_scope?: 'self';
  period?: string;
  date_from?: string;
  date_to?: string;
  type?: string;
  search?: string;
}): Promise<{ blob: Blob; filename: string }> {
  return getBlobExport(
    '/agent/transactions/export',
    {
      owner_scope: params.owner_scope,
      period: params.period,
      date_from: params.date_from,
      date_to: params.date_to,
      type: params.type,
      search: params.search,
    },
    `credit-transactions_${params.date_from ?? 'export'}.csv`,
  );
}

export async function exportAgentOnlineBalanceLedger(params: {
  currency?: string;
  type?: string;
  status?: string;
  query?: string;
  dateFrom: string;
  dateTo: string;
}): Promise<{ blob: Blob; filename: string }> {
  return getBlobExport(
    '/agent/online-balance/ledger/export',
    {
      currency: params.currency,
      type: params.type,
      status: params.status,
      query: params.query,
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
    },
    `usd-wallet-ledger_${params.dateFrom}_to_${params.dateTo}.csv`,
  );
}

export async function fetchAgentShopWalletTransactions(params: {
  shop_id: string;
  page?: number;
  limit?: number;
  sort?: 'asc' | 'desc';
}): Promise<{
  success: boolean;
  data?: { items: ShopWalletLedgerRow[]; pagination: AgentTransactionsPagination };
  message?: string;
}> {
  const { shop_id, ...query } = params;
  const { data } = await client.get(`/agent/shops/${encodeURIComponent(shop_id)}/transactions`, {
    params: query,
  });
  return data;
}

export async function fetchAgentShopCreditTransactionById(
  shopId: string,
  id: string,
): Promise<{ success: boolean; data?: { item: AgentWalletLedgerEntry }; message?: string }> {
  const { data } = await client.get(`/agent/shops/${encodeURIComponent(shopId)}/transactions/${id}`);
  return data;
}

export async function fetchAgentShopUsdLedgerEntryById(
  shopId: string,
  id: string,
): Promise<{ success: boolean; data?: { item: Record<string, unknown> }; message?: string }> {
  const { data } = await client.get(`/agent/shops/${encodeURIComponent(shopId)}/online-balance/ledger/${id}`);
  return data;
}

export async function fetchAgentShopWallets(shopId: string): Promise<{
  success: boolean;
  data?: AgentShopWalletSummary;
  message?: string;
}> {
  const { data } = await client.get(`/agent/shops/${encodeURIComponent(shopId)}/wallets`);
  return data;
}

export async function fetchAgentShopOnlineBalanceLedger(
  shopId: string,
  params?: { currency?: string; type?: string; limit?: number; offset?: number }
): Promise<{ success: boolean; data?: ShopUsdWalletLedgerResponse; message?: string }> {
  const { data } = await client.get(`/agent/shops/${encodeURIComponent(shopId)}/online-balance/ledger`, { params });
  return data;
}

export async function fetchShops(): Promise<{
  success: boolean;
  data?: AgentShop[];
  /** Agent cost rate for this account (0–1). */
  agent_cost_rate?: number;
}> {
  const { data } = await client.get<{
    success: boolean;
    data?: AgentShop[];
    agent_cost_rate?: number;
  }>('/agent/shops');
  return data;
}

export async function fetchAgentPlayers(): Promise<{
  success: boolean;
  data?: AgentPlayer[];
}> {
  const { data } = await client.get('/agent/players');
  return data;
}

export async function fetchAgentPlayer(playerId: string): Promise<{
  success: boolean;
  data?: AgentPlayerDetail;
  message?: string;
}> {
  const { data } = await client.get<{ success: boolean; data?: AgentPlayerDetail; message?: string }>(
    `/agent/players/${encodeURIComponent(playerId)}`
  );
  return data;
}

export async function fetchAgentPlayerTransactions(
  playerId: string,
  params?: { limit?: number }
): Promise<{ success: boolean; data?: AgentPlayerWalletTransaction[]; message?: string }> {
  const { data } = await client.get(`/agent/players/${encodeURIComponent(playerId)}/transactions`, {
    params: params?.limit != null ? { limit: params.limit } : undefined,
  });
  return data;
}

export async function fetchShop(
  shopId: string
): Promise<{ success: boolean; data?: AgentShop; message?: string }> {
  const { data } = await client.get<{ success: boolean; data?: AgentShop; message?: string }>(
    `/agent/shops/${shopId}`
  );
  return data;
}

export async function createShop(payload: {
  name: string;
  username?: string;
  email: string;
  phone?: string;
  password: string;
  cost_rate?: number;
}): Promise<{ success: boolean; data?: AgentShop; message?: string }> {
  const { data } = await client.post('/agent/shops', payload);
  return data;
}

export async function suspendShop(
  shopId: string
): Promise<{ success: boolean; data?: AgentShop; message?: string }> {
  const { data } = await client.patch(`/agent/shops/${shopId}/suspend`);
  return data;
}

export async function unsuspendShop(
  shopId: string
): Promise<{ success: boolean; data?: AgentShop; message?: string }> {
  const { data } = await client.patch(`/agent/shops/${shopId}/unsuspend`);
  return data;
}

export async function updateShopCost(
  shopId: string,
  cost_rate: number
): Promise<{ success: boolean; data?: AgentShop; message?: string }> {
  const { data } = await client.patch<{ success: boolean; data?: AgentShop; message?: string }>(
    `/agent/shops/${shopId}/cost`,
    { cost_rate }
  );
  return data;
}

export async function updateShopName(
  shopId: string,
  name: string
): Promise<{ success: boolean; data?: AgentShop; message?: string }> {
  const { data } = await client.patch<{ success: boolean; data?: AgentShop; message?: string }>(
    `/agent/shops/${shopId}/name`,
    { name }
  );
  return data;
}

export async function updateShopPassword(
  shopId: string,
  password: string
): Promise<{ success: boolean; data?: AgentShop; message?: string }> {
  const { data } = await client.patch<{ success: boolean; data?: AgentShop; message?: string }>(
    `/agent/shops/${shopId}/password`,
    { password }
  );
  return data;
}

export async function updateShopPopupPermission(
  shopId: string,
  can_create_player_popup: boolean
): Promise<{ success: boolean; data?: AgentShop; message?: string }> {
  const { data } = await client.patch<{ success: boolean; data?: AgentShop; message?: string }>(
    `/agent/shops/${shopId}/popup-permissions`,
    { can_create_player_popup }
  );
  return data;
}

/** POST /agent/shops/:shop_id/cash-credits — records cash sale + distributes credits (V1: cash_received locked = expected). */
export async function createShopCashCredits(
  shopId: string,
  payload: {
    credits: number;
    payment_method?: 'cash' | 'bank_transfer' | 'check' | 'other';
    received_at?: string;
    external_reference?: string;
    note?: string;
  }
): Promise<{ success: boolean; data?: unknown; message?: string }> {
  const { data } = await client.post(`/agent/shops/${encodeURIComponent(shopId)}/cash-credits`, payload);
  return data;
}

export async function getAgentCashTransactions(params?: Record<string, unknown>): Promise<{
  success: boolean;
  data?: unknown;
  message?: string;
}> {
  const { data } = await client.get('/agent/cash-transactions', { params });
  return data;
}

export async function getAgentCashTransactionDetail(
  id: string,
  createdAt?: string
): Promise<{ success: boolean; data?: unknown; message?: string }> {
  const { data } = await client.get(`/agent/cash-transactions/${encodeURIComponent(id)}`, {
    params: createdAt ? { created_at: createdAt } : undefined,
  });
  return data;
}

export async function exportAgentCashTransactions(params?: Record<string, unknown>): Promise<{
  blob: Blob;
  filename: string;
}> {
  const response = await client.get<Blob>('/agent/cash-transactions/export', {
    params,
    responseType: 'blob',
  });
  const disposition = String(response.headers['content-disposition'] || '');
  const match = /filename="([^"]+)"/i.exec(disposition);
  const filename = match?.[1] || 'cash-transactions-export.csv';
  return { blob: response.data, filename };
}

export async function withdrawCreditsFromShop(
  shopId: string,
  amount: number,
  note?: string
): Promise<{ success: boolean; data?: unknown; message?: string }> {
  const body: { amount: number; note?: string } = { amount };
  const trimmed = note?.trim();
  if (trimmed) body.note = trimmed;
  const { data } = await client.post(`/agent/shops/${encodeURIComponent(shopId)}/credits/withdraw`, body);
  return data;
}

export async function purchaseCredits(
  amount: number,
  paymentMethod?: string
): Promise<{ success: boolean; data?: AgentCreditPurchaseData; message?: string }> {
  const body: { amount: number; payment_method?: string } = { amount };
  if (paymentMethod && paymentMethod.trim() !== '') {
    body.payment_method = paymentMethod.trim();
  }
  const { data } = await client.post('/agent/credits/purchase', body);
  return data;
}

export async function listZeroxStaticWallets(): Promise<{
  success: boolean;
  data?: { items: ZeroxStaticWallet[] };
  message?: string;
}> {
  const { data } = await client.get('/agent/payments/zeroxprocessing/static-wallets');
  return data;
}

export async function initZeroxStaticWallets(currencies: string[]): Promise<{
  success: boolean;
  data?: { items: ZeroxStaticWallet[] };
  message?: string;
}> {
  const { data } = await client.post('/agent/payments/zeroxprocessing/static-wallets/init', { currencies });
  return data;
}

export type AdminBroadcastRow = {
  id: string;
  title: string;
  body: string;
  createdAt: string | null;
  acknowledged: boolean;
};

export async function fetchAgentBroadcasts(): Promise<{
  success: boolean;
  data?: { broadcasts: AdminBroadcastRow[] };
}> {
  const { data } = await client.get('/agent/broadcasts');
  return data;
}

export async function ackAgentBroadcast(broadcastId: string): Promise<{ success: boolean; message?: string }> {
  const { data } = await client.post(`/agent/broadcasts/${broadcastId}/ack`);
  return data;
}

export async function createAgentBroadcast(payload: {
  title: string;
  body: string;
  targetShopIds?: string[];
  targetUserIds?: string[];
  expiresAt?: string | null;
}): Promise<{
  success: boolean;
  data?: { id: string; title: string; body: string; audience: string; targetIds: string[]; expiresAt: string | null; createdAt: string | null };
  message?: string;
}> {
  const { data } = await client.post('/agent/broadcasts', {
    title: payload.title,
    body: payload.body,
    target_shop_ids: payload.targetShopIds ?? [],
    target_user_ids: payload.targetUserIds ?? [],
    expires_at: payload.expiresAt ?? undefined,
  });
  return data;
}

export type InAppPopupRow = {
  id: string;
  title: string;
  body: string;
  createdAt: string | null;
  displayMode?: 'once' | 'sticky_daily';
  expiresAt?: string | null;
};

/** Modal in-app popups (separate from bell / broadcasts). */
export async function fetchAgentInAppPopupsUnseen(): Promise<{
  success: boolean;
  data?: { popups: InAppPopupRow[] };
}> {
  const { data } = await client.get('/agent/in-app-popups/unseen');
  return data;
}

export async function markAgentInAppPopupSeen(popupId: string): Promise<{ success: boolean; message?: string }> {
  const { data } = await client.post(`/agent/in-app-popups/${popupId}/seen`);
  return data;
}

export async function markAgentInAppPopupNeverAgain(popupId: string): Promise<{ success: boolean; message?: string }> {
  const { data } = await client.post(`/agent/in-app-popups/${popupId}/never-again`);
  return data;
}

export async function createAgentInAppPopup(payload: {
  title: string;
  body: string;
  targetShopIds?: string[];
  targetUserIds?: string[];
  expiresAt?: string | null;
  displayMode?: 'once' | 'sticky_daily';
}): Promise<{
  success: boolean;
  data?: { id: string; title: string; body: string; audience: string; targetIds: string[]; expiresAt: string | null; createdAt: string | null; displayMode?: string };
  message?: string;
}> {
  const { data } = await client.post('/agent/in-app-popups', {
    title: payload.title,
    body: payload.body,
    target_shop_ids: payload.targetShopIds ?? [],
    target_user_ids: payload.targetUserIds ?? [],
    expires_at: payload.expiresAt ?? undefined,
    display_mode: payload.displayMode ?? 'once',
  });
  return data;
}

export async function fetchPublicGameProviders(): Promise<{
  success?: boolean;
  data?: { items: Array<{ id: string; name: string; code: string; status?: string }> };
}> {
  const { data } = await client.get('/game-providers');
  return data;
}

export async function fetchShopGameMappings(shopId: string): Promise<{
  success: boolean;
  data?: { game_ids: string[]; agent_effective_game_ids: string[] };
  message?: string;
}> {
  const { data } = await client.get(`/agent/shops/${shopId}/game-mappings`);
  return data;
}

export async function putShopGameMappings(
  shopId: string,
  gameIds: string[],
): Promise<{ success: boolean; message?: string }> {
  const { data } = await client.put(`/agent/shops/${shopId}/game-mappings`, { game_ids: gameIds });
  return data;
}

export async function fetchAgentPlayerFraudIndicators(params?: Record<string, unknown>) {
  const { data } = await client.get('/agent/player-fraud-indicators', { params });
  return data;
}

export async function fetchAgentPlayerFraudIndicatorDetail(id: string) {
  const { data } = await client.get(`/agent/player-fraud-indicators/${encodeURIComponent(id)}`);
  return data;
}

export async function fetchAgentPlayerFraudWithdrawalAnalysis(transactionId: string) {
  const { data } = await client.get(
    `/agent/player-fraud-indicators/transaction/${encodeURIComponent(transactionId)}/profit-analysis`,
  );
  return data;
}

export async function resolveAgentPlayerFraudIndicator(id: string, notes?: string) {
  const { data } = await client.post(
    `/agent/player-fraud-indicators/${encodeURIComponent(id)}/resolve`,
    { notes: notes || undefined },
  );
  return data;
}
